import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { clipY } from './build';
import { BUILT, type EltModel, INK, T, createElt } from './elt-model';
import { BACKGROUND } from './sky';

// The ELT on its own, for working on the model rather than the hero: an orbit camera, every
// named part toggleable, and the knobs the hero's engine turns (build clock, explode,
// pointing, doors, the print variant's cut) as sliders. Same renderer and bloom as the
// hero, so what looks right here looks right there.

type Settings = {
  clock: number;
  explode: number;
  alt: number;
  az: number;
  domeAz: number;
  domeFollows: boolean;
  doors: number;
  cut: number; // world height; CUT_OFF means no cut
  lasers: boolean;
  lightPath: boolean;
  bloom: boolean;
  grid: boolean;
};

const CUT_OFF = 90;

const DEFAULTS: Settings = {
  clock: BUILT,
  explode: 0,
  alt: 60,
  az: 0,
  domeAz: 0,
  domeFollows: true,
  doors: 1,
  cut: CUT_OFF,
  lasers: false,
  lightPath: false,
  bloom: true,
  grid: false,
};

// The URL can set any numeric or boolean setting (`?alt=45&grid=1`), the starting view
// (`view=front`), the parts to hide by name prefix (`hide=dome,cranes`) and `panel=0`, so
// a link reproduces a view.
function fromUrl() {
  const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
  const settings = { ...DEFAULTS };
  for (const key of Object.keys(DEFAULTS) as Array<keyof Settings>) {
    const raw = params.get(key);
    if (raw === null) continue;
    (settings as Record<string, number | boolean>)[key] = typeof DEFAULTS[key] === 'boolean' ? raw === '1' || raw === 'true' : Number(raw);
  }
  const hide = (params.get('hide') ?? '').split(',').filter(Boolean);
  // `cam=x,y,z&at=x,y,z` places the camera exactly (what the bottom-left readout prints)
  const triple = (raw: string | null) => {
    const n = (raw ?? '').split(',').map(Number);
    return n.length === 3 && n.every(Number.isFinite) ? (n as THREE.Vector3Tuple) : null;
  };
  const cam = triple(params.get('cam'));
  const at = triple(params.get('at'));
  const custom: [THREE.Vector3Tuple, THREE.Vector3Tuple] | null = cam ? [cam, at ?? [0, 32, 0]] : null;
  return { settings, view: params.get('view') ?? '¾', custom, hide, panel: params.get('panel') !== '0' };
}

// [camera position, orbit target], in metres
const VIEWS: Record<string, [THREE.Vector3Tuple, THREE.Vector3Tuple]> = {
  '¾': [[150, 95, 170], [0, 32, 0]],
  front: [[0, 45, -210], [0, 35, 0]],
  side: [[210, 45, 0], [0, 35, 0]],
  top: [[0, 260, 0.01], [0, 0, 0]],
  slit: [[0, 75, -70], [0, 30, 0]],
  M1: [[0, 70, -30], [0, 30, 0]],
  site: [[700, 420, 800], [0, 0, 0]],
};

class Inspector {
  renderer: THREE.WebGLRenderer;
  composer: EffectComposer;
  bloom: UnrealBloomPass;
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(40, 1, 0.5, 20000);
  controls: OrbitControls;
  clock = { value: BUILT };
  elt: EltModel;
  grid: THREE.Group;
  settings = DEFAULTS;
  frame = 0;
  start = performance.now();
  playing = false;
  cranesWanted = true;
  onClock?: (t: number) => void;
  onCamera?: (text: string) => void;

  constructor(private container: HTMLElement) {
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false });
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setClearColor(new THREE.Color(BACKGROUND).convertSRGBToLinear(), 1);
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    container.appendChild(this.renderer.domElement);

    const target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples: 4 });
    this.composer = new EffectComposer(this.renderer, target);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.28, 0.35, 0.55);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());

    this.elt = createElt(this.clock);
    this.scene.add(this.elt.root);

    // 10 m grid over the 150 × 300 m platform, 50 m lines brighter
    this.grid = new THREE.Group();
    const fine = new THREE.GridHelper(300, 30, INK, INK);
    (fine.material as THREE.LineBasicMaterial).transparent = true;
    (fine.material as THREE.LineBasicMaterial).opacity = 0.08;
    const coarse = new THREE.GridHelper(300, 6, INK, INK);
    (coarse.material as THREE.LineBasicMaterial).transparent = true;
    (coarse.material as THREE.LineBasicMaterial).opacity = 0.25;
    this.grid.add(fine, coarse, new THREE.AxesHelper(20));
    this.grid.position.y = 0.2;
    this.scene.add(this.grid);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.maxDistance = 6000;
    this.controls.addEventListener('change', () => this.reportCamera());
    this.view('¾');
    this.resize();
    this.loop();
  }

  view(name: string) {
    this.place(VIEWS[name]);
  }

  place([pos, look]: [THREE.Vector3Tuple, THREE.Vector3Tuple]) {
    this.camera.position.set(...pos);
    this.controls.target.set(...look);
    this.controls.update();
  }

  reportCamera() {
    const p = this.camera.position;
    const t = this.controls.target;
    const f = (v: THREE.Vector3) => `[${v.toArray().map((n) => Math.round(n)).join(', ')}]`;
    this.onCamera?.(`camera ${f(p)} → ${f(t)} · ${Math.round(p.distanceTo(t))} m`);
  }

  apply(s: Settings) {
    this.settings = s;
    if (!this.playing) this.clock.value = s.clock;
    this.elt.setExplode(s.explode);
    this.elt.setPointing(s.az, s.alt, s.domeFollows ? s.az : s.domeAz);
    this.elt.setDoors(s.doors);
    clipY.value = s.cut >= CUT_OFF ? 1e5 : s.cut;
    this.grid.visible = s.grid;
  }

  play() {
    this.playing = true;
    this.clock.value = 0;
  }

  setVisible(name: string, visible: boolean) {
    for (const p of this.elt.parts) if (p.name === name) p.visible = visible;
  }

  loop = () => {
    this.frame = requestAnimationFrame(this.loop);
    const time = (performance.now() - this.start) / 1000;
    if (this.playing) {
      this.clock.value = Math.min(BUILT, this.clock.value + 1 / 60);
      this.onClock?.(this.clock.value);
      if (this.clock.value >= BUILT) this.playing = false;
    }
    const s = this.settings;
    // the cranes set their own visibility from their amount, so their toggle goes through here
    this.elt.setCranes(this.clock.value, this.cranesWanted ? 1 : 0);
    this.elt.setLasers(s.lasers ? 1 : 0, time);
    this.elt.setLightPath(s.lightPath ? 1 : 0, time);
    this.controls.update();
    if (s.bloom) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
  };

  resize() {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.renderer.setSize(width, height, false);
    this.composer.setSize(width, height);
    this.bloom.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  dispose() {
    cancelAnimationFrame(this.frame);
    clipY.value = 1e5;
    this.controls.dispose();
    this.elt.dispose();
    this.composer.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}

export function TelescopeInspector() {
  const mount = useRef<HTMLDivElement>(null);
  const inspector = useRef<Inspector | null>(null);
  const [initial] = useState(fromUrl);
  const [settings, setSettings] = useState(initial.settings);
  const [parts, setParts] = useState<Array<{ name: string; visible: boolean }>>([]);
  const [camera, setCamera] = useState('');
  const [open, setOpen] = useState(initial.panel);

  useEffect(() => {
    if (!mount.current) return;
    const it = new Inspector(mount.current);
    inspector.current = it;
    it.onClock = (t) => setSettings((s) => ({ ...s, clock: t }));
    it.onCamera = setCamera;
    if (initial.custom) it.place(initial.custom);
    else if (initial.view in VIEWS) it.view(initial.view);
    it.reportCamera();
    const names = [...new Set(it.elt.parts.map((p) => p.name))];
    const list = names.map((name) => ({ name, visible: !initial.hide.some((prefix) => name.startsWith(prefix)) }));
    for (const p of list) {
      if (p.name === 'cranes') it.cranesWanted = p.visible;
      it.setVisible(p.name, p.visible);
    }
    setParts(list);
    const observer = new ResizeObserver(() => it.resize());
    observer.observe(mount.current);
    return () => {
      observer.disconnect();
      it.dispose();
      inspector.current = null;
    };
  }, []);

  useEffect(() => {
    inspector.current?.apply(settings);
  }, [settings]);

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => setSettings((s) => ({ ...s, [key]: value }));

  const toggle = (name: string, solo: boolean) => {
    setParts((list) => {
      const next = list.map((p) => ({ ...p, visible: solo ? p.name === name : p.name === name ? !p.visible : p.visible }));
      for (const p of next) {
        if (p.name === 'cranes' && inspector.current) inspector.current.cranesWanted = p.visible;
        inspector.current?.setVisible(p.name, p.visible);
      }
      return next;
    });
  };

  const showAll = (visible: boolean) => {
    setParts((list) => {
      const next = list.map((p) => ({ ...p, visible }));
      for (const p of next) {
        if (p.name === 'cranes' && inspector.current) inspector.current.cranesWanted = visible;
        inspector.current?.setVisible(p.name, visible);
      }
      return next;
    });
  };

  return (
    <div className="relative h-dvh w-full overflow-hidden font-mono text-[11px]" style={{ background: BACKGROUND, color: INK }}>
      <div ref={mount} className="absolute inset-0" />

      <div className="pointer-events-none absolute bottom-3 left-3 opacity-70">
        {camera} · drag orbit · right-drag pan · scroll zoom
      </div>

      <button type="button" onClick={() => setOpen((o) => !o)} className="absolute top-3 right-3 z-10 border border-current px-2 py-1 uppercase tracking-widest">
        {open ? 'ocultar' : 'panel'}
      </button>

      {open && (
        <aside className="absolute top-12 right-3 bottom-3 flex w-72 flex-col gap-4 overflow-y-auto border border-current/30 bg-black/70 p-3 backdrop-blur">
          <Section title="vista">
            <div className="flex flex-wrap gap-1">
              {Object.keys(VIEWS).map((name) => (
                <Chip key={name} onClick={() => inspector.current?.view(name)}>
                  {name}
                </Chip>
              ))}
            </div>
          </Section>

          <Section title="partes">
            <div className="flex gap-1">
              <Chip onClick={() => showAll(true)}>todas</Chip>
              <Chip onClick={() => showAll(false)}>ninguna</Chip>
              <span className="self-center opacity-50">⌥ clic = solo</span>
            </div>
            <div className="flex flex-col gap-0.5">
              {parts.map((p) => (
                <label key={p.name} className="flex cursor-pointer items-center gap-2" onClick={(e) => {
                  e.preventDefault();
                  toggle(p.name, e.altKey);
                }}>
                  <span className="inline-block size-2.5 border border-current" style={{ background: p.visible ? INK : 'transparent' }} />
                  <span className={p.visible ? '' : 'opacity-40'}>{p.name}</span>
                </label>
              ))}
            </div>
          </Section>

          <Section title="construcción">
            <Slider label="reloj" value={settings.clock} min={0} max={BUILT} step={0.05} onChange={(v) => set('clock', v)} format={(v) => `${v.toFixed(2)} s`} />
            <div className="flex flex-wrap gap-1">
              <Chip onClick={() => inspector.current?.play()}>▶ construir</Chip>
              <Chip onClick={() => set('clock', BUILT)}>terminado</Chip>
            </div>
            <div className="flex flex-wrap gap-1 opacity-70">
              {Object.entries(T)
                .filter(([key]) => key !== 'm1Dur')
                .map(([key, at]) => (
                  <Chip key={key} onClick={() => set('clock', at + 0.6)}>
                    {key}
                  </Chip>
                ))}
            </div>
            <Slider label="explotar" value={settings.explode} min={0} max={1} step={0.01} onChange={(v) => set('explode', v)} />
            <Slider label="corte y" value={settings.cut} min={-5} max={CUT_OFF} step={0.5} onChange={(v) => set('cut', v)} format={(v) => (v >= CUT_OFF ? 'off' : `${v} m`)} />
          </Section>

          <Section title="apuntado">
            <Slider label="altitud" value={settings.alt} min={0} max={90} step={1} onChange={(v) => set('alt', v)} format={(v) => `${v}°`} />
            <Slider label="azimut" value={settings.az} min={-180} max={180} step={1} onChange={(v) => set('az', v)} format={(v) => `${v}°`} />
            <Check label="cúpula sigue al azimut" checked={settings.domeFollows} onChange={(v) => set('domeFollows', v)} />
            {!settings.domeFollows && (
              <Slider label="cúpula" value={settings.domeAz} min={-180} max={180} step={1} onChange={(v) => set('domeAz', v)} format={(v) => `${v}°`} />
            )}
            <Slider label="puertas" value={settings.doors} min={0} max={1} step={0.01} onChange={(v) => set('doors', v)} />
          </Section>

          <Section title="extras">
            <Check label="láseres" checked={settings.lasers} onChange={(v) => set('lasers', v)} />
            <Check label="camino de luz" checked={settings.lightPath} onChange={(v) => set('lightPath', v)} />
            <Check label="bloom" checked={settings.bloom} onChange={(v) => set('bloom', v)} />
            <Check label="grilla 10 m + ejes" checked={settings.grid} onChange={(v) => set('grid', v)} />
            <Chip onClick={() => setSettings({ ...DEFAULTS })}>reset</Chip>
          </Section>
        </aside>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="uppercase tracking-[0.2em] opacity-60">{title}</h2>
      {children}
    </section>
  );
}

function Chip({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="border border-current/50 px-1.5 py-0.5 hover:bg-current/15">
      {children}
    </button>
  );
}

function Slider(props: { label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void; format?: (v: number) => string }) {
  const { label, value, min, max, step, onChange, format = (v) => v.toFixed(2) } = props;
  return (
    <label className="flex flex-col gap-0.5">
      <span className="flex justify-between">
        <span>{label}</span>
        <span className="opacity-60">{format(value)}</span>
      </span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ accentColor: INK }} />
    </label>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: INK }} />
      {label}
    </label>
  );
}
